import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { FormControlLabel } from "@remo-co/ui-core/src/components/FormControlLabel";
import { Switch } from "@remo-co/ui-core/src/components/Switch";
import { useTheme } from "@remo-co/ui-core/src/hooks/useTheme";
import { Error } from "@remo-co/ui-core/src/icons/Error";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { Link } from "@remo-co/ui-core/src/components/Link";
import { useI18n } from "i18n";
import { useUpdateLoginOptions } from "modules/companySettings";
import { useSelector } from "react-redux";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { selectLoginOptionsConfiguration } from "modules/companySettings/redux/selectors";
import isCustomSsoEnabled from "modules/companyPlanSettings/utils/isCustomSsoEnabled";
import { LoginOptions } from "@remo-co/types";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { useStyles } from "./styles";

const EditLoginOptions = () => {
  const dispatch = useAppDispatch();
  const loginOptionsConfiguration = useSelector(
    selectLoginOptionsConfiguration,
  );

  const theme = useTheme();
  const styles = useStyles();
  const { t } = useI18n(["settings", "common", "url"]);

  const { myCompanyPlan } = useCompanyPlanSettings();
  const customSsoEnabled = isCustomSsoEnabled(myCompanyPlan);

  const { updateLoginOptions } = useUpdateLoginOptions();

  const loginOptions = Object.values(LoginOptions)
    .filter((option) =>
      option === LoginOptions.CUSTOM_SSO ? customSsoEnabled : true,
    )
    .sort((a, b) => {
      if (a === LoginOptions.EMAIL_LINK || a === LoginOptions.EDC) return 1;
      if (b === LoginOptions.EMAIL_LINK || b === LoginOptions.EDC) return -1;
      return 0;
    });

  const handleChange = (option: LoginOptions) => {
    if (
      option === LoginOptions.MICROSOFT &&
      !loginOptionsConfiguration[option]
    ) {
      dispatch(
        addDialogNotification({
          title: t("common:warning"),
          content: (
            <div className={styles.microsoftDisclaimer}>
              <span className={styles.disclaimerHeader}>
                <Typography variant="h5" className={styles.disclaimerTitle}>
                  {t("settings:about.microsoft.login")}
                </Typography>
                <Error className={styles.disclaimerIcon} />
              </span>
              <Typography variant="subtitle1" className={styles.disclaimerText}>
                {t("settings:microsoft.login.disclaimer")}
              </Typography>
              <Typography variant="subtitle1" className={styles.disclaimerText}>
                {t("settings:microsoft.login.disclaimer.1")}
              </Typography>
              <Typography variant="subtitle1" className={styles.disclaimerText}>
                {t("settings:microsoft.login.disclaimer.2")}
              </Typography>
              <Link
                href={t("url:microsoft.login.common.issues")}
                openInNewTab
                className={styles.link}
              >
                {t("settings:microsoft.link.click")}
              </Link>
            </div>
          ),
          actionsClassName: styles.disclaimerActions,
          confirmBtnClassName: styles.disclaimerActionButtons,
          dismissBtnClassName: styles.disclaimerActionButtons,
          hideCloseButton: true,
          confirmText: "Yes, I understand",
          dismissText: t("common:button.cancel"),
          onConfirm: () => {
            updateLoginOptions({
              [option]: !loginOptionsConfiguration[option],
            });
          },
          confirmTestId: "confirm-microsoft-login",
        }),
      );
      return;
    }

    updateLoginOptions({
      [option]: !loginOptionsConfiguration[option],
    });
  };

  return (
    <Box marginBottom={theme.spacing(8)}>
      <Typography variant="h3" className={styles.heading}>
        {t("settings:login.options.header")}
      </Typography>
      <Typography variant="h5" className={styles.subtitle}>
        {t("settings:login.options.description.1")}
      </Typography>
      <Typography variant="h5" className={styles.subtitle2}>
        {t("settings:login.options.description.2")}
      </Typography>

      {loginOptions.map((option) => (
        <div key={option} className={styles.optionContainer}>
          <Tooltip
            title={
              option === LoginOptions.EMAIL_LINK || option === LoginOptions.EDC
                ? t("settings:login.option.cannot.disable")
                : ""
            }
          >
            <FormControlLabel
              label={t(`settings:login.options.${option}`)}
              labelPlacement="start"
              classes={{
                label: styles.label,
              }}
              control={
                <Switch
                  color="primary"
                  controlled
                  checked={
                    option === LoginOptions.EMAIL_LINK ||
                    option === LoginOptions.EDC
                      ? true
                      : loginOptionsConfiguration[option]
                  }
                  disabled={
                    option === LoginOptions.EMAIL_LINK ||
                    option === LoginOptions.EDC
                  }
                  onChange={() => handleChange(option)}
                  testId={`${option}-login-switch`}
                />
              }
            />
          </Tooltip>
        </div>
      ))}
    </Box>
  );
};

export default EditLoginOptions;
